import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ADMIN_NAV } from '../navigation/adminNav';

const KEYS = {
  favorites: 'bh-admin-favorites',
  pinned: 'bh-admin-pinned',
  recent: 'bh-admin-recent',
  locale: 'bh-admin-locale',
  sidebarCollapsed: 'bh-admin-sidebar-collapsed',
} as const;

export type AdminLocale = 'en' | 'fil';

type AdminPreferencesValue = {
  favorites: string[];
  pinned: string[];
  recent: string[];
  locale: AdminLocale;
  sidebarCollapsed: boolean;
  toggleFavorite: (path: string) => void;
  togglePinned: (path: string) => void;
  setLocale: (locale: AdminLocale) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
};

function readJsonArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function validPaths(paths: string[]): string[] {
  const allowed = new Set(ADMIN_NAV.map((n) => n.path));
  return paths.filter((p) => allowed.has(p));
}

const AdminPreferencesContext = createContext<AdminPreferencesValue | null>(null);

export const AdminPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [favorites, setFavorites] = useState<string[]>(() =>
    validPaths(readJsonArray(KEYS.favorites))
  );
  const [pinned, setPinned] = useState<string[]>(() => validPaths(readJsonArray(KEYS.pinned)));
  const [recent, setRecent] = useState<string[]>(() => validPaths(readJsonArray(KEYS.recent)));
  const [locale, setLocaleState] = useState<AdminLocale>(() => {
    const v = localStorage.getItem(KEYS.locale);
    return v === 'fil' ? 'fil' : 'en';
  });
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() =>
    localStorage.getItem(KEYS.sidebarCollapsed) === '1'
  );

  useEffect(() => {
    localStorage.setItem(KEYS.favorites, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem(KEYS.pinned, JSON.stringify(pinned));
  }, [pinned]);

  useEffect(() => {
    localStorage.setItem(KEYS.recent, JSON.stringify(recent));
  }, [recent]);

  useEffect(() => {
    localStorage.setItem(KEYS.locale, locale);
    document.documentElement.lang = locale === 'fil' ? 'fil' : 'en';
  }, [locale]);

  useEffect(() => {
    localStorage.setItem(KEYS.sidebarCollapsed, sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    const p = location.pathname.replace(/\/$/, '') || '/';
    if (!p.startsWith('/admin') || p === '/admin/login') return;
    const known = ADMIN_NAV.some((n) => n.path === p);
    if (!known && p !== '/admin') return;
    setRecent((prev) => {
      const next = [p, ...prev.filter((x) => x !== p)].slice(0, 8);
      return next;
    });
  }, [location.pathname]);

  const toggleFavorite = useCallback((path: string) => {
    setFavorites((prev) => (prev.includes(path) ? prev.filter((x) => x !== path) : [...prev, path]));
  }, []);

  const togglePinned = useCallback((path: string) => {
    setPinned((prev) => {
      if (prev.includes(path)) return prev.filter((x) => x !== path);
      return [...prev, path].slice(0, 5);
    });
  }, []);

  const setLocale = useCallback((next: AdminLocale) => setLocaleState(next), []);
  const setSidebarCollapsed = useCallback((c: boolean) => setSidebarCollapsedState(c), []);
  const toggleSidebarCollapsed = useCallback(
    () => setSidebarCollapsedState((v) => !v),
    []
  );

  const value = useMemo(
    () => ({
      favorites,
      pinned,
      recent,
      locale,
      sidebarCollapsed,
      toggleFavorite,
      togglePinned,
      setLocale,
      setSidebarCollapsed,
      toggleSidebarCollapsed,
    }),
    [
      favorites,
      pinned,
      recent,
      locale,
      sidebarCollapsed,
      toggleFavorite,
      togglePinned,
      setLocale,
      setSidebarCollapsed,
      toggleSidebarCollapsed,
    ]
  );

  return (
    <AdminPreferencesContext.Provider value={value}>{children}</AdminPreferencesContext.Provider>
  );
};

export function useAdminPreferences(): AdminPreferencesValue {
  const ctx = useContext(AdminPreferencesContext);
  if (!ctx) {
    throw new Error('useAdminPreferences must be used within AdminPreferencesProvider');
  }
  return ctx;
}
